import { CaseStatus, EndReasonId, SystemEndReason, } from '@debbie/common/dist/cases/index.js';
import { BulkActionType, } from '@debbie/common/dist/insights/bulk-actions.js';
import { TenantType } from '@debbie/common/dist/tenants/index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import * as insightsAPI from '@debbie/cortex/dist/api/insights.js';
import { useEndReasons } from '@debbie/cortex/dist/end-reasons/index.js';
import { selectExclusiveContent } from '@debbie/cortex/dist/exclusiveContent/selectors.js';
import { h } from '@debbie/cortex/dist/index.js';
import { useMe } from '@debbie/cortex/dist/me/index.js';
import { useProcedures } from '@debbie/cortex/dist/procedures/index.js';
import { useTenant } from '@debbie/cortex/dist/tenant/index.js';
import { useUpdateTypesMap } from '@debbie/cortex/dist/update-types/index.js';
import { useWorkflows } from '@debbie/cortex/dist/workflows/index.js';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { errorNotification } from '../../utils/errors.js';
import BillingAppendixSetupSelect from '../BillingAppendixSetupSelect.js';
import BillingModelSelect from '../BillingModelSelect.js';
import Button from '../Button.js';
import CaseGroupSelect from '../CaseGroupSelect.js';
import ExclusiveContent from '../ExclusiveContent.js';
import Input from '../Input.js';
import QueueListProgress from '../QueueListProgress.js';
import SearchInput from '../SearchInput.js';
import Select from '../Select.js';
import UpdateTypeSelect from '../UpdateTypeSelect.js';
import Well from '../Well.js';
const BulkActions = (props) => {
    const { caseFilter } = props;
    const lang = useSelector((s) => s.lang);
    const proceduresLoadable = useProcedures();
    const procedures = h.hasData(proceduresLoadable)
        ? proceduresLoadable.data
        : null;
    const exclusiveContent = useSelector(selectExclusiveContent);
    const meLoadable = useMe();
    const me = h.hasData(meLoadable) ? meLoadable.data : null;
    const workflowsLoadable = useWorkflows();
    const workflows = h.hasData(workflowsLoadable) ? workflowsLoadable.data : null;
    const tenant = useTenant();
    const updateTypesMapLoadable = useUpdateTypesMap();
    const updateTypesMap = h.hasData(updateTypesMapLoadable)
        ? updateTypesMapLoadable.data
        : {};
    const endReasons = useEndReasons();
    const [bulkActionId, setBulkActionId] = useState(null);
    const [bulkActionStarted, setBulkActionStarted] = useState(false);
    const [bulkActionType, setBulkAction] = useState(null);
    const [caseStatus, setCaseStatus] = useState(CaseStatus.VERIFIED);
    const [endReason, setEndReason] = useState(EndReasonId[SystemEndReason.ErrorCreditor]);
    const [updateTypeId, setUpdateTypeId] = useState(null);
    const [updateComment, setUpdateComment] = useState('');
    const [procedureId, setProcedureId] = useState(null);
    const [billingModelId, setBillingModelId] = useState(null);
    const [billingAppendixSetupId, setBillingAppendixSetupId] = useState(null);
    const [workflowId, setWorkflowId] = useState(null);
    const [targetTaskId, setTargetTaskId] = useState(null);
    const [caseGroupId, setCaseGroupId] = useState(null);
    const selectedProcedure = procedures && procedures.find((p) => p.procedureId === procedureId);
    const selectedWorkflow = workflows &&
        workflows.find((workflow) => workflow.workflowId === workflowId);
    const inWorkflowIds = caseFilter.inWorkflowIds;
    const selectedWorkflows = inWorkflowIds
        ? workflows?.filter((workflow) => inWorkflowIds.includes(workflow.workflowId))
        : workflows;
    const tasks = selectedWorkflows
        ?.reduce((acc, workflow) => {
        for (let i = 0; i < workflow.tasks.length; i++) {
            const task = workflow.tasks[i];
            const updateType = updateTypesMap && updateTypesMap[task.updateTypeId];
            if (updateType) {
                acc = acc.concat({
                    task,
                    updateType,
                    position: task.position,
                });
            }
        }
        return acc;
    }, [])
        .sort((a, b) => a.position.y - b.position.y);
    const selectedTargetTask = tasks?.find((task) => task.task.id === targetTaskId);
    useEffect(() => {
        setBulkActionStarted(false);
    }, [caseFilter]);
    const runBulkActionMutation = useMutation(async () => {
        const action = getBulkAction();
        if (!action) {
            throw Error('Error');
        }
        const bulkAction = await insightsAPI.caseFilterBulk({ ...caseFilter, ...action?.caseFilter }, action?.bulkAction);
        setBulkActionId(bulkAction.bulkActionId);
        setBulkActionStarted(true);
    }, {
        onError: (err) => {
            errorNotification(lang, err, 'create', lang.errorEntities.bulkActions);
        },
    });
    const bulkActionEditors = {
        [BulkActionType.MoveCaseStatus]: (React.createElement("div", null,
            React.createElement(Select, { value: caseStatus, onChange: (e) => setCaseStatus(e.target.value) },
                React.createElement("option", { value: CaseStatus.VERIFIED }, lang.cases[CaseStatus.VERIFIED]),
                React.createElement("option", { value: CaseStatus.ENDED }, lang.cases[CaseStatus.ENDED])),
            CaseStatus.ENDED === caseStatus && (React.createElement(Select, { value: endReason, onChange: (e) => setEndReason(e.target.value) }, (h.hasData(endReasons) ? endReasons.data : []).map((reason) => (React.createElement("option", { key: reason.id, value: reason.id }, lang.endReason[reason.handle] ??
                reason.label))))))),
        [BulkActionType.PauseCases]: (React.createElement(React.Fragment, null,
            React.createElement(UpdateTypeSelect, { value: updateTypeId, onChange: (updateTypeId) => setUpdateTypeId(updateTypeId), placeholder: lang.cases.pauseReason }),
            React.createElement(Input, { placeholder: lang.cases.pauseComment, value: updateComment, onChange: (e) => setUpdateComment(e.target.value) }))),
        [BulkActionType.ContinueCases]: (React.createElement(UpdateTypeSelect, { value: updateTypeId, onChange: (updateTypeId) => setUpdateTypeId(updateTypeId), placeholder: lang.cases.pauseReason })),
        [BulkActionType.RunProcedure]: (React.createElement(SearchInput, { forceOpen: true, placeholder: lang.procedures.selectedProcedure, onSelect: (id) => setProcedureId(id), searchAll: (q) => {
                return (procedures || [])
                    .filter((procedure) => JSON.stringify(procedure)
                    .toLowerCase()
                    .indexOf(q.toLowerCase()) !== -1 || q.length === 0)
                    .map((procedure) => ({
                    key: procedure.procedureId,
                    heading: procedure.title,
                    subheading: procedure.description,
                    returnData: procedure.procedureId,
                }));
            }, static: selectedProcedure && selectedProcedure.title, onRemoveStatic: () => setProcedureId(null), cacheResetTrigger: BulkActionType.RunProcedure })),
        [BulkActionType.ChangeBillingModel]: (React.createElement(BillingModelSelect, { placeholder: lang.bulkActions.bulkActionBillingModelPlaceholder, value: billingModelId, onChange: setBillingModelId })),
        [BulkActionType.ChangeBillingAppendixSetup]: (React.createElement(BillingAppendixSetupSelect, { value: billingAppendixSetupId, onChange: setBillingAppendixSetupId })),
        [BulkActionType.ChangeWorkflow]: (React.createElement(SearchInput, { forceOpen: true, placeholder: lang.bulkActions.bulkActionWorkflowPlaceholder, onSelect: (id) => setWorkflowId(id), searchAll: (q) => {
                return (workflows || [])
                    .filter((workflow) => JSON.stringify(workflow)
                    .toLowerCase()
                    .indexOf(q.toLowerCase()) !== -1 || q.length === 0)
                    .map((workflow) => ({
                    key: workflow.workflowId,
                    heading: workflow.title,
                    subheading: workflow.description,
                    returnData: workflow.workflowId,
                }));
            }, static: selectedWorkflow && selectedWorkflow.title, onRemoveStatic: () => setWorkflowId(null), cacheResetTrigger: BulkActionType.ChangeWorkflow })),
        [BulkActionType.MoveCaseToWorkflowTask]: inWorkflowIds?.length === 1 ? (React.createElement(SearchInput, { forceOpen: true, placeholder: lang.bulkActions.bulkActionMoveToTaskInWorkflowPlaceholder, onSelect: (id) => setTargetTaskId(id), searchAll: (q) => {
                return (tasks || [])
                    .filter((task) => JSON.stringify(task)
                    .toLowerCase()
                    .indexOf(q.toLowerCase()) !== -1 || q.length === 0)
                    .map((task) => ({
                    key: task.task.id,
                    heading: task.updateType.label,
                    returnData: task.task.id,
                }));
            }, static: selectedTargetTask && selectedTargetTask.updateType.label, onRemoveStatic: () => setTargetTaskId(null), cacheResetTrigger: BulkActionType.MoveCaseToWorkflowTask })) : (React.createElement("p", null, lang.bulkActions.pleaseSelectAWorkflow)),
        [BulkActionType.RollbackOneTask]: React.createElement(React.Fragment, null),
        [BulkActionType.RollbackAllTasks]: React.createElement(React.Fragment, null),
        [BulkActionType.RunBillingModel]: React.createElement(React.Fragment, null),
        [BulkActionType.ResubscribeCosmo]: React.createElement(React.Fragment, null),
        [BulkActionType.DeleteRKIRegistrationsOnEndedCases]: React.createElement(React.Fragment, null),
        [BulkActionType.MoveRKIRegistrationsToMergeCases]: React.createElement(React.Fragment, null),
        [BulkActionType.ResyncCosmo]: React.createElement(React.Fragment, null),
        [BulkActionType.ChangeCaseGroup]: (React.createElement(CaseGroupSelect, { placeholder: lang.bulkActions.bulkActionChangeCaseGroup, value: caseGroupId, onChange: setCaseGroupId })),
    };
    const getBulkAction = () => {
        if (!bulkActionType) {
            return null;
        }
        switch (bulkActionType) {
            case BulkActionType.MoveCaseStatus:
                if (caseStatus === CaseStatus.ENDED) {
                    return {
                        bulkAction: {
                            type: BulkActionType.MoveCaseStatus,
                            details: {
                                status: CaseStatus.ENDED,
                                endReasonId: endReason,
                            },
                        },
                    };
                }
                if (caseStatus === CaseStatus.VERIFIED) {
                    return {
                        bulkAction: {
                            type: BulkActionType.MoveCaseStatus,
                            details: {
                                status: CaseStatus.VERIFIED,
                                endReasonId: null,
                            },
                        },
                    };
                }
                return null;
            case BulkActionType.PauseCases:
                if (!updateTypeId) {
                    return null;
                }
                return {
                    bulkAction: {
                        type: BulkActionType.PauseCases,
                        details: {
                            updateTypeId,
                            update: updateComment || undefined,
                        },
                    },
                };
            case BulkActionType.ContinueCases:
                if (!updateTypeId) {
                    return null;
                }
                return {
                    bulkAction: {
                        type: BulkActionType.ContinueCases,
                        details: {
                            updateTypeId,
                        },
                    },
                };
            case BulkActionType.RunProcedure:
                if (!procedureId) {
                    return null;
                }
                return {
                    bulkAction: {
                        type: BulkActionType.RunProcedure,
                        details: { procedureId },
                    },
                };
            case BulkActionType.ChangeBillingModel:
                if (!billingModelId) {
                    return null;
                }
                return {
                    bulkAction: {
                        type: BulkActionType.ChangeBillingModel,
                        details: { billingModelId },
                    },
                };
            case BulkActionType.ChangeBillingAppendixSetup:
                if (!billingAppendixSetupId) {
                    return null;
                }
                return {
                    bulkAction: {
                        type: BulkActionType.ChangeBillingAppendixSetup,
                        details: { billingAppendixSetupId },
                    },
                };
            case BulkActionType.ChangeWorkflow:
                if (!workflowId) {
                    return null;
                }
                return {
                    bulkAction: {
                        type: BulkActionType.ChangeWorkflow,
                        details: { workflowId },
                    },
                };
            case BulkActionType.MoveCaseToWorkflowTask:
                if (!(inWorkflowIds?.length === 1 && targetTaskId)) {
                    return null;
                }
                if (!(caseFilter?.inWorkflowIds && caseFilter?.inWorkflowIds[0])) {
                    return null;
                }
                return {
                    bulkAction: {
                        type: BulkActionType.MoveCaseToWorkflowTask,
                        details: {
                            workflowId: caseFilter.inWorkflowIds[0],
                            targetTaskId,
                        },
                    },
                };
            case BulkActionType.RollbackOneTask:
                return {
                    bulkAction: {
                        type: BulkActionType.RollbackOneTask,
                        details: {},
                    },
                };
            case BulkActionType.RollbackAllTasks:
                return {
                    bulkAction: {
                        type: BulkActionType.RollbackAllTasks,
                        details: {},
                    },
                };
            case BulkActionType.RunBillingModel:
                return {
                    bulkAction: {
                        type: BulkActionType.RunBillingModel,
                        details: {},
                    },
                };
            case BulkActionType.ResubscribeCosmo:
                return {
                    bulkAction: {
                        type: BulkActionType.ResubscribeCosmo,
                        details: {},
                    },
                };
            case BulkActionType.DeleteRKIRegistrationsOnEndedCases:
                return {
                    bulkAction: {
                        type: BulkActionType.DeleteRKIRegistrationsOnEndedCases,
                        details: {},
                    },
                };
            case BulkActionType.MoveRKIRegistrationsToMergeCases:
                return {
                    bulkAction: {
                        type: BulkActionType.MoveRKIRegistrationsToMergeCases,
                        details: {},
                    },
                };
            case BulkActionType.ResyncCosmo:
                return {
                    bulkAction: {
                        type: BulkActionType.ResyncCosmo,
                        details: {},
                    },
                };
            case BulkActionType.ChangeCaseGroup:
                if (!caseGroupId) {
                    return null;
                }
                return {
                    bulkAction: {
                        type: BulkActionType.ChangeCaseGroup,
                        details: {
                            caseGroupId,
                        },
                    },
                };
        }
    };
    const hasExclusiveContent = !!(me && exclusiveContent?.includes(me.id));
    return (React.createElement("div", null,
        React.createElement(Select, { value: bulkActionType, onChange: (e) => {
                setBulkActionStarted(false);
                setBulkAction(e.target.value);
            } },
            React.createElement("option", { value: "" },
                "- ",
                lang.bulkActions.bulkActions,
                " -"),
            Object.values(BulkActionType).map((_bulkActionType) => {
                if ([
                    BulkActionType.RunBillingModel,
                    BulkActionType.ResubscribeCosmo,
                    BulkActionType.MoveRKIRegistrationsToMergeCases,
                    BulkActionType.ResyncCosmo,
                ].includes(_bulkActionType) &&
                    !hasExclusiveContent) {
                    return null;
                }
                // Hide for tenant.type=AccountsReceivable
                if ([
                    BulkActionType.ChangeBillingModel,
                    BulkActionType.ChangeBillingAppendixSetup,
                ].includes(_bulkActionType) &&
                    tenant?.type === TenantType.AccountsReceivable &&
                    !hasExclusiveContent) {
                    return null;
                }
                return (React.createElement("option", { key: _bulkActionType, value: _bulkActionType }, lang.bulkActionTypes[_bulkActionType]));
            })),
        bulkActionType && (React.createElement(Well, null, bulkActionStarted ? (React.createElement("div", null,
            React.createElement("p", null, lang.bulkActions.bulkActionStarted),
            React.createElement(ExclusiveContent, null, bulkActionId && (React.createElement(QueueListProgress, { listHandlerId: bulkActionId }))))) : (React.createElement("div", null,
            React.createElement("p", null, lang.bulkActionDescriptions[bulkActionType]),
            bulkActionEditors[bulkActionType],
            React.createElement(Button, { loading: runBulkActionMutation.isLoading, disabled: !getBulkAction(), onClick: () => {
                    if (confirm(lang.bulkActions.bulkActionsConfirm)) {
                        runBulkActionMutation.mutate();
                    }
                }, block: true }, lang.bulkActions.runBulkAction)))))));
};
export default BulkActions;
//# sourceMappingURL=BulkActions.js.map